import { Injectable, Optional, SkipSelf } from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Alert, AlertType } from '@app/core/alert/alert.model';

@Injectable({ providedIn: 'root' }) export class AlertService {
    private subject = new Subject<Alert>();
    private defaultId = 'default-alert';
    private autoClearSub?: Subscription;    

    constructor(@Optional() @SkipSelf() core?:AlertService){
        if (core) {
            throw new Error("You should import core module only in the root module")
        }
      }

    // enable subscribing to alerts observable
    onAlert(id = this.defaultId): Observable<Alert> {
        return this.subject.asObservable().pipe(filter(x => x && x.id === id));
    }

    // convenience methods
    success(message: string, options?: Partial<Alert>) {
        this.alert(message, AlertType.Success, options);
    }

    error(message: string, options?: Partial<Alert>) {
        this.alert(message, AlertType.Error, options);
    }

    info(message: string, options?: Partial<Alert>) {
        this.alert(message, AlertType.Info, options);
    }

    warn(message: string, options?: Partial<Alert>) {
        this.alert(message, AlertType.Warning, options);
    }
    
    // main alert method
    alert(message: string, type: AlertType, options: Partial<Alert> = {}) {
        const id = options.id || this.defaultId;
        const autoClose = options.autoClose ?? true;
        const duration = options.autoClose === false ? 0 : 3_000;

        const alert = new Alert(
            id,
            type,
            message,
            autoClose,
            options.keepAfterRouteChange
        );

        // emit alert
        this.subject.next(alert);

        // clear existing timer
        this.autoClearSub?.unsubscribe();

        // auto clear
        if (autoClose && duration > 0) {
            this.autoClearSub = timer(duration).subscribe(() => {
                this.clear(id);
            });
        }
    }


    // clear alerts
    clear(id = this.defaultId) {
        this.subject.next(new Alert(id));
    }
}